import { FC, ReactElement, useMemo } from 'react';
import { Accordion, Button, Col, Form, Row } from 'react-bootstrap';
import i18next from 'i18next';
import { ExerciseCategoryDto } from '../../../../../../shared/dtos/exerciseCategory.dto';
import { ExerciseTagDto } from '../../../../../../shared/dtos/exerciseTag.dto';
import { ExerciseFilterDto } from '../../../../../../shared/dtos/exerciseFilter.dto';

type ExercisePickerSidebarProps = {
  filters?: ExerciseFilterDto;
  openCategoryId: string | null;
  selectedByCategory: Record<string, string[]>;
  hasActiveFilters: boolean;
  onToggleCategory: (categoryId: string) => void;
  onToggleTag: (tag: ExerciseTagDto, checked: boolean) => void;
  onResetFilters: () => void;
};

type CategoryWithTags = {
  category: ExerciseCategoryDto;
  tags: ExerciseTagDto[];
};

const ExercisePickerSidebar: FC<ExercisePickerSidebarProps> = ({
  filters,
  openCategoryId,
  selectedByCategory,
  hasActiveFilters,
  onToggleCategory,
  onToggleTag,
  onResetFilters,
}: ExercisePickerSidebarProps): ReactElement => {
  const userLanguage: string = i18next.language;

  const categoriesWithTags: CategoryWithTags[] = useMemo(() => {
    const categories: ExerciseCategoryDto[] = filters?.categories ?? [];
    const tagsByCategory: Record<string, ExerciseTagDto[]> = filters?.tags ?? {};

    return categories
      .map((category: ExerciseCategoryDto) => ({ category, tags: tagsByCategory[category.id] ?? [] }))
      .filter((entry: CategoryWithTags) => entry.tags.length > 0);
  }, [filters]);

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span className="fw-semibold fs-5">{i18next.t('exerciseModal.filterText')}</span>

        <Button
          variant="link"
          size="sm"
          className="p-0 text-decoration-none"
          style={{ fontSize: '1.3rem', color: '#a0a3b1' }}
          disabled={!hasActiveFilters}
          onClick={onResetFilters}
        >
          {i18next.t('exerciseModal.filterClearText')}
        </Button>
      </div>

      {categoriesWithTags.length === 0 ? (
        <div className="text-muted small">{i18next.t('exerciseModal.noFilters')}</div>
      ) : (
        <Accordion activeKey={openCategoryId ?? ''}>
          {categoriesWithTags.map(({ category, tags }: CategoryWithTags) => {
            const selectedCount: number = selectedByCategory[category.id]?.length ?? 0;

            return (
              <Accordion.Item eventKey={category.id} key={`cat-${category.id}`}>
                <Accordion.Header onClick={() => onToggleCategory(category.id)}>
                  <span className="fw-semibold fs-5">
                    {userLanguage === 'en' ? category.en : category.de}
                    {selectedCount > 0 && <span className="badge bg-primary ms-2 align-middle">{selectedCount}</span>}
                  </span>
                </Accordion.Header>

                <Accordion.Body className="pt-2">
                  {tags.map((tag: ExerciseTagDto) => (
                    <Row key={`tag-${tag.id}`} className="mb-1">
                      <Col md={12}>
                        <Form.Check
                          id={`tag-${tag.id}`}
                          type="checkbox"
                          name={`tag-${tag.id}`}
                          className="fw-normal fs-5"
                          checked={(selectedByCategory[tag.categoryId] ?? []).includes(tag.id)}
                          label={userLanguage === 'en' ? tag.en : tag.de}
                          onChange={(event) => onToggleTag(tag, event.target.checked)}
                        />
                      </Col>
                    </Row>
                  ))}
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      )}
    </div>
  );
};

export default ExercisePickerSidebar;
